import { ContinuousScrollViewer } from 'features/knowledgeDocumentViewer/components/viewer/continuous/ContinuousScrollViewer'
import { PagedDocumentViewer } from 'features/knowledgeDocumentViewer/components/viewer/PagedDocumentViewer'
import type { SearchHighlightData } from 'features/knowledgeDocumentViewer/components/viewer/search/SearchHighlightOverlay'
import { DocumentViewerRoot } from 'features/knowledgeDocumentViewer/components/viewer/shared/styles'
import {
  type DocumentViewerHandle,
  type FrameTarget,
  type ViewerCommonProps,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import { type ViewLayout } from 'features/knowledgeDocumentViewer/types'
import React, { forwardRef } from 'react'

const noopPageChange = () => {}

export type DocumentViewerProps = ViewerCommonProps & {
  documentId: string
  isActiveTab: boolean
  pageNumber: number
  pdfUrl: string
  scale: number
  totalPages: number
  showMultiplePages: boolean
  viewLayout: ViewLayout
  searchHighlights?: SearchHighlightData
  frameTarget?: FrameTarget
  /** Called when the viewer has centered + zoomed the frame target. */
  onFrameApplied?: () => void
  onPageChange?: (page: number) => void
}

export const DocumentViewer = forwardRef<
  DocumentViewerHandle,
  DocumentViewerProps
>(function DocumentViewer(
  {
    documentId,
    isActiveTab,
    pageNumber,
    pdfUrl,
    scale,
    totalPages,
    showMultiplePages,
    viewLayout,
    searchHighlights,
    frameTarget,
    onFrameApplied,
    onPageChange,
    highlightBoxIds,
    onBoxClick,
    onBoxAuxClick,
    onBoxContextMenu,
    onBoxHover,
    onMultipleBoxSelect,
    onAliasClick,
    onDocReferenceClick,
    onDocReferenceHover,
    actionMode,
    showTooltips = false,
  },
  ref
) {
  if (viewLayout === 'continuous' && totalPages > 1) {
    return (
      <DocumentViewerRoot>
        <ContinuousScrollViewer
          ref={ref}
          documentId={documentId}
          pageNumber={pageNumber}
          pdfUrl={pdfUrl}
          scale={scale}
          totalPages={totalPages}
          onPageChange={onPageChange ?? noopPageChange}
          isActiveTab={isActiveTab}
          searchHighlights={searchHighlights}
          frameTarget={frameTarget}
          onFrameApplied={onFrameApplied}
          highlightBoxIds={highlightBoxIds}
          onBoxClick={onBoxClick}
          onBoxAuxClick={onBoxAuxClick}
          onBoxContextMenu={onBoxContextMenu}
          onBoxHover={onBoxHover}
          onMultipleBoxSelect={onMultipleBoxSelect}
          onAliasClick={onAliasClick}
          onDocReferenceClick={onDocReferenceClick}
          onDocReferenceHover={onDocReferenceHover}
          actionMode={actionMode}
          showTooltips={showTooltips}
        />
      </DocumentViewerRoot>
    )
  }

  return (
    <DocumentViewerRoot>
      <PagedDocumentViewer
        ref={ref}
        documentId={documentId}
        isActiveTab={isActiveTab}
        pageNumber={pageNumber}
        pdfUrl={pdfUrl}
        scale={scale}
        totalPages={totalPages}
        showMultiplePages={showMultiplePages}
        searchHighlights={searchHighlights}
        frameTarget={frameTarget}
        onFrameApplied={onFrameApplied}
        highlightBoxIds={highlightBoxIds}
        onBoxClick={onBoxClick}
        onBoxAuxClick={onBoxAuxClick}
        onBoxContextMenu={onBoxContextMenu}
        onBoxHover={onBoxHover}
        onMultipleBoxSelect={onMultipleBoxSelect}
        onAliasClick={onAliasClick}
        onDocReferenceClick={onDocReferenceClick}
        onDocReferenceHover={onDocReferenceHover}
        actionMode={actionMode}
        showTooltips={showTooltips}
      />
    </DocumentViewerRoot>
  )
})
